import { useEffect, useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type LoginResult } from '../lib/api'
import { isCopilotDeviceCode, isCopilotLoginUrl } from '../lib/copilot-login-url'

type CtaState = 'idle' | 'confirm' | 'running' | 'done' | 'failed'

/** The manual fallback shown on failure — what a member can run themselves from a terminal. */
const COPILOT_LOGIN_CMD = 'copilot login'

/** Tail kept on screen — a failed sign-in's useful line is almost always at the END. */
const LOG_TAIL_CHARS = 2000

/**
 * The assisted GitHub Copilot sign-in — shown on AiAgentSection's Copilot card when the passerelle
 * says Copilot is signed out (or was just installed from that page), and in the chat panel's
 * sign-in banner when Copilot is the agent the chat would use.
 *
 * Same state machine as ClaudeLoginCta, one step of consent ahead of anything running on the
 * member's machine: idle ("Sign in") → confirm → running → done | failed. What differs is the
 * running state: `copilot login` takes nothing back from Niobe, so there is no code field. Its
 * browser open can silently do nothing from inside Niobe's hidden child process, so the running
 * state always offers the sign-in page as a link (`login-url`) and, for GitHub's device flow, the
 * code to type there (`login-code`) — both re-checked here before they are rendered.
 *
 * `target="_blank"` matters in Desktop: the window's `setWindowOpenHandler` routes that to the
 * system browser, whereas a plain `href` would navigate the Electron window away from the app.
 */
export function CopilotLoginCta({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useLang()
  const [state, setState] = useState<CtaState>('idle')
  const [jobId, setJobId] = useState<string | null>(null)
  /** Only set when triggerLogin() itself throws (no job was ever created to poll for a result). */
  const [triggerError, setTriggerError] = useState<string | null>(null)
  const [copied, setCopied] = useState<'code' | 'cmd' | null>(null)
  const logId = useId()

  const jobQuery = useQuery({
    queryKey: ['agent-job', jobId],
    queryFn: () => api.agentJob(jobId as string),
    enabled: state === 'running' && !!jobId,
    refetchInterval: 2000,
  })
  const job = jobQuery.data
  const result = job?.result as LoginResult | undefined

  useEffect(() => {
    if (state !== 'running' || !job) return
    if (job.status === 'cancelled') {
      // The member asked to stop — back to idle, not an error state.
      setState('idle')
      setJobId(null)
    } else if (job.status === 'done') {
      // 'done' only means loginCopilot() resolved — the CLI's own exit code is the verdict.
      if (result?.ok) {
        setState('done')
        onSignedIn()
      } else {
        setState('failed')
      }
    } else if (job.status === 'error') {
      setState('failed')
    }
  }, [state, job, result, onSignedIn])

  async function confirmLogin(): Promise<void> {
    setTriggerError(null)
    try {
      const { jobId: id } = await api.triggerLogin('copilot')
      setJobId(id)
      setState('running')
    } catch (e) {
      setTriggerError(e instanceof Error ? e.message : String(e))
      setState('failed')
    }
  }

  async function cancelLogin(): Promise<void> {
    if (jobId) await api.cancelJob(jobId)
  }

  async function copy(text: string, what: 'code' | 'cmd'): Promise<void> {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(what)
      setTimeout(() => setCopied(null), 1800)
    } catch {
      /* clipboard unavailable — the text is still selectable on screen */
    }
  }

  if (state === 'idle') {
    return (
      <button className="mini ok" onClick={() => setState('confirm')}>
        {t('claude_login_cta')}
      </button>
    )
  }

  if (state === 'confirm') {
    return (
      <div className="claude-login" role="status" aria-live="polite">
        <p className="claude-login-title">
          <b>{t('copilot_login_confirm_title')}</b>
        </p>
        <p>{t('copilot_login_confirm_body')}</p>
        <div className="claude-login-actions">
          <button className="mini ok" onClick={() => void confirmLogin()}>
            {t('claude_login_confirm_yes')}
          </button>
          <button className="mini ghost" onClick={() => setState('idle')}>
            {t('claude_install_confirm_cancel')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'running') {
    const progress = [...(job?.progress ?? [])].reverse()
    const loginUrl = progress
      .map((p) => (p.tool === 'login-url' ? p.target : undefined))
      .find((target): target is string => isCopilotLoginUrl(target))
    const deviceCode = progress
      .map((p) => (p.tool === 'login-code' ? p.target : undefined))
      .find((target): target is string => isCopilotDeviceCode(target))
    return (
      <div className="claude-login" role="status" aria-live="polite">
        <span className="thinking">
          <span className="dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {t('claude_login_running')}
        </span>
        <p className="claude-login-fallback-label">{t('claude_login_no_browser')}</p>
        {loginUrl && (
          <a className="claude-login-link" href={loginUrl} target="_blank" rel="noopener noreferrer">
            {t('claude_login_open_page')}
          </a>
        )}
        {deviceCode && (
          <>
            <p className="claude-login-code-label">{t('copilot_login_code_label')}</p>
            <div className="claude-login-actions">
              <code className="mono claude-login-cmd">{deviceCode}</code>
              <button className="mini ok" onClick={() => void copy(deviceCode, 'code')}>
                {copied === 'code' ? t('ext_copied') : t('copilot_login_copy_code')}
              </button>
            </div>
          </>
        )}
        <div className="claude-login-actions">
          <button className="mini ghost" onClick={() => void cancelLogin()}>
            {t('stop_generating')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'done') {
    return (
      <div className="claude-login" role="status" aria-live="polite">
        <p className="claude-login-success">
          {result?.account
            ? t('copilot_login_done_account').replace('{account}', () => result.account as string)
            : t('claude_login_done')}
        </p>
      </div>
    )
  }

  // state === 'failed'
  const log = (result?.log ?? triggerError ?? '').slice(-LOG_TAIL_CHARS)
  return (
    <div className="claude-login" role="status" aria-live="polite">
      <p className="claude-login-failed-title">
        <b>{t('claude_login_failed')}</b>
      </p>
      {log !== '' && (
        <>
          <p id={logId} className="claude-login-log-label">
            {t('claude_install_log_label')}
          </p>
          <pre className="mono claude-login-log" tabIndex={0} aria-labelledby={logId}>
            {log}
          </pre>
        </>
      )}
      <div className="claude-login-actions">
        <button className="mini ok" onClick={() => void confirmLogin()}>
          {t('claude_login_retry')}
        </button>
      </div>
      <p>{t('claude_login_manual_label')}</p>
      <div className="claude-login-actions">
        <code className="mono claude-login-cmd">{COPILOT_LOGIN_CMD}</code>
        <button className="mini ok" onClick={() => void copy(COPILOT_LOGIN_CMD, 'cmd')}>
          {copied === 'cmd' ? t('ext_copied') : t('copilot_login_copy_cmd')}
        </button>
      </div>
    </div>
  )
}
